import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Target, Search, Flame, RefreshCw, Zap } from "lucide-react";
import { StatusBadge, StatusSelect } from "../components/LeadStatusControls";
import { STATUS_META, STATUS_OPTIONS } from "../lib/leadStatus";

function AutoChips({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {items.map((a, i) => (
        <span key={i} data-testid="lead-automation-chip"
          className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-[#128C7E]/10 text-[#075E54] border border-[#128C7E]/25 inline-flex items-center gap-1">
          <Zap className="h-2.5 w-2.5" />{a.name}{a.status === "paused" ? " (paused)" : ""}
        </span>
      ))}
    </div>
  );
}

function leadBadge(cat) {
  const map = {
    "Very Hot": "bg-red-500/10 text-red-600 border-red-500/20",
    Hot: "bg-red-500/10 text-red-600 border-red-500/20",
    Warm: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
    Cold: "bg-[#F0F2F5] text-[#54656F] border-[#E9EDEF]",
  };
  return map[cat] || map.Cold;
}
const isHot = (cat) => ["Hot", "Very Hot"].includes(cat);
const rankOf = (cat) => (cat === "Very Hot" ? "Hot" : cat || "Cold");

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
function inDateBucket(dateStr, bucket, from, to) {
  if (bucket === "all") return true;
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const today = startOfDay(new Date());
  const yest = new Date(today); yest.setDate(today.getDate() - 1);
  if (bucket === "today") return d >= today;
  if (bucket === "yesterday") return d >= yest && d < today;
  if (bucket === "custom") {
    let ok = true;
    if (from) ok = ok && d >= startOfDay(from);
    if (to) { const t = startOfDay(to); t.setDate(t.getDate() + 1); ok = ok && d < t; }
    return ok;
  }
  return true;
}
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short" }) + ", " + new Date(s).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—");

const selCls = "text-xs bg-white border border-[#E9EDEF] rounded-lg px-2.5 py-2 text-[#374151] focus:outline-none focus:border-[#25D366]";

export default function Leads() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [labels, setLabels] = useState([]);
  const [search, setSearch] = useState("");
  const [rank, setRank] = useState("all");
  const [label, setLabel] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateBucket, setDateBucket] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [syncing, setSyncing] = useState(false);

  const loadLeads = () => api.get("/leads").then((r) => setRows(r.data)).catch(() => {});
  const onLeadUpdated = (l) => setRows((prev) => prev.map((r) => (r.lead_id === l.lead_id ? { ...r, ...l } : r)));
  useEffect(() => {
    loadLeads();
    api.get("/labels").then((r) => setLabels(r.data || [])).catch(() => {});
  }, []);

  const syncLeads = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/leads/audit");
      const d = r.data || {};
      toast.success(`Synced ${d.scanned_conversations || 0} chats · ${d.leads_created || 0} new leads · ${d.total_leads || 0} total`);
      await loadLeads();
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };

  const counts = useMemo(() => ({
    Cold: rows.filter((r) => rankOf(r.category) === "Cold").length,
    Warm: rows.filter((r) => rankOf(r.category) === "Warm").length,
    Hot: rows.filter((r) => isHot(r.category)).length,
  }), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows.filter((r) => {
      if (rank !== "all" && rankOf(r.category) !== rank) return false;
      if (statusFilter !== "all" && (r.status || "new") !== statusFilter) return false;
      if (label !== "all" && !(r.labels || []).includes(label)) return false;
      if (!inDateBucket(r.updated_at || r.created_at, dateBucket, from, to)) return false;
      if (q && !((r.name || "").toLowerCase().includes(q) || (r.phone || "").toLowerCase().includes(q))) return false;
      return true;
    });
    out = [...out];
    if (sortBy === "score") out.sort((a, b) => (b.score || 0) - (a.score || 0));
    else if (sortBy === "name") out.sort((a, b) => (a.name || a.phone || "").localeCompare(b.name || b.phone || ""));
    else out.sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
    return out;
  }, [rows, search, rank, statusFilter, label, dateBucket, from, to, sortBy]);

  const statusCounts = useMemo(() => {
    const m = {};
    rows.forEach((r) => { const s = r.status || "new"; m[s] = (m[s] || 0) + 1; });
    return m;
  }, [rows]);

  const openLead = (l) => { if (l.conversation_id) navigate(`/inbox?conversation=${l.conversation_id}`); };

  const ScoreBadge = ({ l }) => (
    <span className={`inline-flex items-center gap-1 font-mono text-[10px] px-2 py-0.5 rounded-lg border whitespace-nowrap ${leadBadge(l.category)}`}>
      {isHot(l.category) && <Flame className="h-3 w-3" />}{l.score} · {rankOf(l.category)}
    </span>
  );

  return (
    <>
      <PageHeader eyebrow="Lead Intelligence" title="Leads"
        actions={
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-[#075E54] bg-[#E7FCE8] rounded-full px-3 py-1.5" data-testid="leads-total-counter">
              <Target className="h-3.5 w-3.5" /> {rows.length} total
            </span>
            <button onClick={syncLeads} disabled={syncing} data-testid="leads-sync-btn"
              className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[#075E54] hover:bg-[#128C7E] rounded-full px-3 py-1.5 disabled:opacity-50">
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing..." : "Sync inbox"}
            </button>
          </div>
        } />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 space-y-5">
        {/* Stat cards (tap to filter) */}
        <div className="grid grid-cols-3 gap-3">
          {[["Cold", counts.Cold], ["Warm", counts.Warm], ["Hot 🔥", counts.Hot]].map(([lbl, n], i) => {
            const key = ["Cold", "Warm", "Hot"][i];
            return (
              <button key={lbl} data-testid={`lead-stat-${key}`} onClick={() => setRank(rank === key ? "all" : key)}
                className={`text-left border rounded-xl p-3 md:p-4 transition-colors ${rank === key ? "border-[#25D366] bg-[#E7FCE8]" : "border-[#E9EDEF] bg-white hover:bg-[#F8FAFC]"}`}>
                <p className="font-mono text-[9px] md:text-[10px] uppercase tracking-[0.12em] text-[#8696A0]">{lbl}</p>
                <p className="font-heading text-2xl md:text-3xl font-light text-[#111B21] mt-1">{n}</p>
              </button>
            );
          })}
        </div>

        {/* Organiser */}
        <div className="border border-[#E9EDEF] bg-white rounded-xl p-3 space-y-3" data-testid="lead-organiser">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8696A0]" />
            <input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone"
              className="w-full pl-9 pr-3 py-2 text-sm bg-[#F0F2F5] border border-transparent focus:border-[#25D366] rounded-lg focus:outline-none text-[#111B21]" />
          </div>
          <div className="flex flex-wrap gap-2">
            <select data-testid="lead-sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selCls}>
              <option value="recent">Sort: Most recent</option>
              <option value="score">Sort: Highest score</option>
              <option value="name">Sort: Name (A–Z)</option>
            </select>
            <select data-testid="lead-date-filter" value={dateBucket} onChange={(e) => setDateBucket(e.target.value)} className={selCls}>
              <option value="all">Any date</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="custom">Custom range</option>
            </select>
            <select data-testid="lead-rank-filter" value={rank} onChange={(e) => setRank(e.target.value)} className={selCls}>
              <option value="all">All ranks</option>
              <option value="Hot">🔥 Hot</option>
              <option value="Warm">Warm</option>
              <option value="Cold">Cold</option>
            </select>
            <select data-testid="lead-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selCls}>
              <option value="all">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{STATUS_META[s].label}{statusCounts[s] ? ` (${statusCounts[s]})` : ""}</option>
              ))}
            </select>
            <select data-testid="lead-label-filter" value={label} onChange={(e) => setLabel(e.target.value)} className={selCls}>
              <option value="all">All labels</option>
              {labels.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          {dateBucket === "custom" && (
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#54656F]" data-testid="lead-custom-range">
              <span>From</span>
              <input type="date" data-testid="lead-date-from" value={from} onChange={(e) => setFrom(e.target.value)} className={selCls} />
              <span>To</span>
              <input type="date" data-testid="lead-date-to" value={to} onChange={(e) => setTo(e.target.value)} className={selCls} />
            </div>
          )}
          <p className="text-[11px] text-[#8696A0]">{filtered.length} of {rows.length} leads</p>
        </div>

        {/* Mobile: cards */}
        <div className="space-y-2.5 md:hidden" data-testid="leads-cards">
          {filtered.length === 0 && (
            <div className="border border-[#E9EDEF] bg-white rounded-xl px-5 py-10 text-center text-sm text-[#8696A0]">
              <Target className="h-5 w-5 mx-auto mb-2 text-[#54656F]" />No leads match your filters.
            </div>
          )}
          {filtered.map((l) => (
            <button key={l.lead_id} data-testid="lead-card" onClick={() => openLead(l)}
              className="w-full text-left border border-[#E9EDEF] bg-white rounded-xl p-3.5 active:bg-[#F8FAFC] transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#111B21] truncate">{l.name || l.phone}</p>
                  <p className="text-xs font-mono text-[#8696A0] truncate">{l.phone}</p>
                </div>
                <ScoreBadge l={l} />
              </div>
              <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                <StatusSelect lead={l} onUpdated={onLeadUpdated} />
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-[#54656F]">
                <span>{l.service || "General"}</span>
                <span className="text-[#8696A0]">{l.source || "WhatsApp"}</span>
                <span>Intent: {l.intent || "—"}</span>
              </div>
              {(l.labels || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {l.labels.slice(0, 4).map((x) => (
                    <span key={x} className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#E7FCE8] text-[#075E54] border border-[#25D366]/30">{x}</span>
                  ))}
                </div>
              )}
              <AutoChips items={l.active_automations} />
              <p className="text-[10px] text-[#8696A0] mt-2 font-mono">{fmtDate(l.updated_at || l.created_at)}</p>
            </button>
          ))}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block border border-[#E9EDEF] bg-white rounded-lg overflow-x-auto">
          <table className="w-full text-left" data-testid="leads-table">
            <thead>
              <tr className="border-b border-[#E9EDEF] font-mono text-[10px] uppercase tracking-[0.15em] text-[#8696A0]">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Labels</th>
                <th className="px-5 py-3 font-medium">Source</th>
                <th className="px-5 py-3 font-medium">Intent</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E9EDEF]">
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-[#8696A0]">
                  <Target className="h-5 w-5 mx-auto mb-2 text-[#54656F]" />No leads match your filters.
                </td></tr>
              )}
              {filtered.map((l) => (
                <tr key={l.lead_id} onClick={() => openLead(l)} className="hover:bg-[#F0F2F5] transition-colors cursor-pointer">
                  <td className="px-5 py-3">
                    <p className="text-sm text-[#111B21] font-medium">{l.name || l.phone}</p>
                    <p className="text-xs font-mono text-[#8696A0]">{l.phone}</p>
                  </td>
                  <td className="px-5 py-3"><StatusSelect lead={l} onUpdated={onLeadUpdated} /></td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[180px]">
                      {(l.labels || []).slice(0, 3).map((x) => (
                        <span key={x} className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#E7FCE8] text-[#075E54] border border-[#25D366]/30">{x}</span>
                      ))}
                      {(l.labels || []).length === 0 && <span className="text-xs text-[#8696A0]">—</span>}
                    </div>
                    <AutoChips items={l.active_automations} />
                  </td>
                  <td className="px-5 py-3 text-sm text-[#8696A0]">{l.source || "WhatsApp"}</td>
                  <td className="px-5 py-3 text-sm text-[#54656F]">{l.intent || "—"}</td>
                  <td className="px-5 py-3 text-xs font-mono text-[#8696A0] whitespace-nowrap">{fmtDate(l.updated_at || l.created_at)}</td>
                  <td className="px-5 py-3"><ScoreBadge l={l} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
